import { validateExploitationAgeAndDate } from '@/shared/utils'

// Validation function for Roblox Exploitation Details
export const validateRobloxExploitationDetails = (data: any) => {
  const errors: { [key: string]: string } = {}
  
  // Exploitation types validation
  if (!data.exploitationTypes || data.exploitationTypes.length === 0) {
    errors.exploitationTypes = 'At least one exploitation type must be selected'
  } else if (data.exploitationTypes.includes('None of the Above')) {
    errors.exploitationTypes = 'DQ: Selecting "None of the Above" disqualifies from this campaign'
  }

  // Other injuries validation
  if (data.hasOtherInjuries === undefined || data.hasOtherInjuries === null) {
    errors.hasOtherInjuries = 'Other injuries status is required'
  }

  // Location validation
  if (!data.injuredPartyResidentState) {
    errors.injuredPartyResidentState = 'Injured party resident state is required'
  } else if (data.injuredPartyResidentState.toLowerCase() === 'florida') {
    errors.injuredPartyResidentState = 'DQ: Florida residents are not eligible for this campaign'
  }

  if (!data.injuryOccurredState) {
    errors.injuryOccurredState = 'State where injury occurred is required'
  } else if (data.injuryOccurredState.toLowerCase() === 'florida') {
    errors.injuryOccurredState = 'DQ: Injuries that occurred in Florida are not eligible for this campaign'
  }

  // Timeline validation with comprehensive age/date logic
  if (!data.exploitationStartDate) {
    errors.exploitationStartDate = 'Exploitation start date is required'
  } else {
    // First, check basic date range requirements (3 weeks to 2 years ago)
    // This applies regardless of age logic
    const today = new Date()
    const incidentDate = new Date(data.exploitationStartDate)
    
    if (isNaN(incidentDate.getTime())) {
      errors.exploitationStartDate = 'Invalid date format'
    } else {
      const threeWeeksAgo = new Date(today.getTime() - (3 * 7 * 24 * 60 * 60 * 1000))
      const twoYearsAgo = new Date(today.getTime() - (2 * 365 * 24 * 60 * 60 * 1000))
      
      // Check if date is less than 3 weeks ago
      if (incidentDate > threeWeeksAgo) {
        errors.exploitationStartDate = 'DQ: Event happened too recently - exploitation must have started at least 3 weeks ago'
      }
      // Check if date is more than 2 years ago
      else if (incidentDate < twoYearsAgo) {
        errors.exploitationStartDate = 'DQ: Event happened too long ago - exploitation must have started within the last 2 years'
      }
      // Check if date is in the future
      else if (incidentDate > today) {
        errors.exploitationStartDate = 'DQ: Exploitation start date cannot be in the future'
      }
      // If basic date range is valid, then check age/date logic if DOB is available
      else if (!data.injuredPartyDOB) {
        // Date range is valid but DOB is missing - this is okay, we'll validate separately
      } else {
        // Use comprehensive validation that checks both date and age requirements
        // This validates:
        // - For adults (18+): age must be >16 and <18 at incident, AND incident within 2 years
        // - For minors (<18): age must be >3 and <18 at incident
        // - Date must be at least 3 weeks ago (already checked above)
        const ageDateValidation = validateExploitationAgeAndDate(
          data.exploitationStartDate,
          data.injuredPartyDOB,
          data.injuredPartyAgeAtExploitation
        )
        
        if (!ageDateValidation.isValid && ageDateValidation.errorMessage) {
          // Check if error is about date timing (too recently or too long ago)
          // Note: We've already checked this above, so this should only be age-related
          if (ageDateValidation.errorMessage.includes('too recently') || 
              ageDateValidation.errorMessage.includes('too long ago')) {
            // This shouldn't happen since we checked above, but handle it anyway
            errors.exploitationStartDate = ageDateValidation.errorMessage
          } else {
            // Age-related errors (too young, too old) go to age field
            errors.injuredPartyAgeAtExploitation = ageDateValidation.errorMessage
          }
        }
      }
    }
  }

  // Check if age field is missing when we have the required data
  if (!data.injuredPartyAgeAtExploitation && data.exploitationStartDate && data.injuredPartyDOB) {
    errors.injuredPartyAgeAtExploitation = 'Age at time of exploitation is required'
  }

  // Roblox account validation
  if (data.hasOwnRobloxAccount === undefined || data.hasOwnRobloxAccount === null) {
    errors.hasOwnRobloxAccount = 'Roblox account ownership status is required'
  }

  if (data.knowsRobloxUsername === undefined || data.knowsRobloxUsername === null) {
    errors.knowsRobloxUsername = 'Roblox username knowledge status is required'
  }

  // Roblox username validation - required if user knows their username
  if (data.knowsRobloxUsername === true) {
    if (!data.robloxUsername || data.robloxUsername.trim() === '') {
      errors.robloxUsername = 'Roblox username is required when user knows their username'
    }
  }

  if (data.hasRobloxAccountAccess === undefined || data.hasRobloxAccountAccess === null) {
    errors.hasRobloxAccountAccess = 'Roblox account access status is required'
  }

  if (data.incidentThroughAnotherAccount === undefined || data.incidentThroughAnotherAccount === null) {
    errors.incidentThroughAnotherAccount = 'Incident through another account status is required'
  }

  // Exploitation cause validation - required if incident did NOT occur through another account
  if (data.incidentThroughAnotherAccount === false) {
    if (!data.exploitationCause || data.exploitationCause.trim() === '') {
      errors.exploitationCause = 'Exploitation cause is required when incident did not occur through another account'
    }
  }

  // Communication types validation
  if (!data.communicationTypes || data.communicationTypes.length === 0) {
    errors.communicationTypes = 'At least one communication type must be selected'
  } else if (data.communicationTypes.some((type: string) => type.toLowerCase().includes('none of the above'))) {
    errors.communicationTypes = 'DQ: Selecting "None of the above" disqualifies from this campaign'
  }

  // Exploitation description validation
  if (!data.exploitationDescription || data.exploitationDescription.trim() === '') {
    errors.exploitationDescription = 'Exploitation description is required'
  }

  // Perpetrator information validation
  if (data.metPerpetratorOnRoblox === undefined || data.metPerpetratorOnRoblox === null) {
    errors.metPerpetratorOnRoblox = 'Perpetrator meeting location is required'
  } else if (data.metPerpetratorOnRoblox === false) {
    errors.metPerpetratorOnRoblox = 'DQ: Perpetrator must have been met on Roblox platform'
  }

  if (data.metPerpetratorInPerson === undefined || data.metPerpetratorInPerson === null) {
    errors.metPerpetratorInPerson = 'In-person meeting status is required'
  }

  // Sexual assault validation - required if they met in person
  if (data.metPerpetratorInPerson === true) {
    if (!data.sexuallyAssaultedInPerson || data.sexuallyAssaultedInPerson.trim() === '') {
      errors.sexuallyAssaultedInPerson = 'Description of what happened when they met in person is required'
    }
  }

  if (data.policeReportFiled === undefined || data.policeReportFiled === null) {
    errors.policeReportFiled = 'Police report filing status is required'
  }

  return errors
}
